import styled, { css } from 'styled-components/macro';
import { css as CSS } from '@styled-system/css';
import modularScale from 'styles/helpers/modular-scale';
import { ThemeProps } from 'types/styled';
import { definitionLabel } from 'styles/base-classes';
import Box from 'components/elements/Box';
import colors from 'styles/themes/colors';
import BaseButton from 'components/elements/button';
import Span from 'components/elements/span';
import variables from 'styles/themes/variables';
import { ReactComponent as IconClearFilter } from 'icons/icon-clear-filter.svg';
import { ReactComponent as CloseIcon } from 'icons/icon-close.svg';
import { ReactComponent as ToggleCaret } from 'icons/icon-toggle-caret.svg';
import { ReactComponent as DSPCharts } from 'icons/dsp-charts-icon.svg';
import { SearchFieldContainer } from 'components/styled/SearchField/SearchField';
import { StyledGlobe, FlagImage } from 'components/base/CountryFlag';
import { StyledIcon } from 'components/base/CustomerIcon/CustomerIcon';

export const DesktopFiltersBox = styled(Box)`
  width: ${({ theme }): string => `calc(100% - ${theme.spacing.medium} * 2)`};
  margin: ${({ theme }): string => `${theme.spacing.medium} auto`};

  @media screen and (min-width: 50em) {
    width: ${({ theme }): string => `${theme.layout.smallScreen * 100}%`};
  }

  @media screen and (min-width: 80em) {
    width: ${({ theme }): string => `${theme.layout.largeScreen * 100}%`};
  }

  .selectedItemBox {
    border: ${({ theme }): string => `1px solid ${theme.colors.AccentGreen}`};
  }

  ${SearchFieldContainer} {
    margin: ${({ theme }): string =>
      `${theme.spacing.small} ${theme.spacing.small}`} !important;
  }
`;

export const ClearButton = styled(BaseButton)<any>`
  ${({ isSmallScreen }): any => css`
    ${CSS({
      display: 'inline-flex',
      width: ['100%', 'initial'],
      alignItems: 'center',
      fontSize: 16 /* equal to default user agent font size */,
      fontWeight: '600',
      py: 3,
      px: [4, 5],
      borderColor: 'LightGrey',
      borderStyle: 'solid',
      borderTopWidth: !isSmallScreen ? ['1px', '0'] : ['0', '0'],
      marginTop: [1, 0],
      marginLeft: [0, 'auto'],
      color: 'ShadowBlack',
    })}
  `}

  @media screen and (max-width: 47em) {
    justify-content: center;
    width: 100%;
  }
`;

export const ClearButtonCloseIcon = styled(CloseIcon)`
  transform: translateY(0px) scale(0.7);
  color: ${({ theme }): string => theme.colors.AccentGreen};
  margin-bottom: 1px;

  g {
    stroke-width: 3px;
  }
`;

export const IconClear = styled(IconClearFilter)<any>`
  background: #ffffff;
  min-width: 12px;
  min-height: 12px;
  padding: 2px;
  border-radius: 50%;
  cursor: pointer;

  g {
    stroke-width: 0.5px;
    stroke: rgb(30, 146, 132);

    g {
      fill: rgb(30, 146, 132);
    }
  }

  ${({ isSmallScreen, top }): string =>
    isSmallScreen &&
    `
    position: absolute;
    right: 16px;
    top: ${top ? top : 0};
  `}
`;

export const RecentFilter = styled.span<ThemeProps<'span'>>`
  margin-left: auto;
  ${definitionLabel};
  font-weight: 600;
  align-self: center;
`;

export const Bubble = styled(Span)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${colors.NotificationOrange};
  position: absolute;
  top: -0.3em;
  right: 0.1em;
  font-size: ${modularScale(-4.5)};
  font-family: ${variables.numbersTypography};
  font-weight: 600;
  width: 1.5em;
  height: 1.5em;
  color: white;

  @media screen and (min-width: 47em) {
    top: 1.5em;
    right: -0.7em;
  }
`;

export const Button = styled(BaseButton)<any>`
  ${({ isActive, theme, isSmallScreen, isClearButton }): any => css`
    ${CSS({
      color: isActive && !isClearButton ? 'AccentGreen' : 'NightBlue',
      height: ['initial', 'auto'],
      width: '100%',
      '&:hover, &:focus': {
        bg: '#fafafa',
      },
      px: isSmallScreen ? '1.3rem' : 2,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    })}

    ${isClearButton &&
    CSS({
      '&:active': {
        color: isActive ? 'AccentGreen' : 'NightBlue',
      },
    })}

    svg {
      ${CSS({
        marginRight: theme.spacing.xsmall,
        verticalAlign: 'middle',
      })}
    }
  `}
`;

export const EntityTypeTitle = styled.p<any>`
  font-family: Proxima Nova;
  font-size: 11px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
  letter-spacing: 1px;
  line-height: 17px;
  margin: 0
    ${({ isSmallScreen }): string => (isSmallScreen ? '15px 0' : '0 2px')} 0;
  ${({ alignSelf }): string => alignSelf && `align-self: ${alignSelf};`}

  ${({ resetButton }): string =>
    resetButton &&
    `
    display: flex;
    align-items: center;
    gap: 8px;

    svg {
      margin-bottom: 3px;
    }
  `}
`;

export const Title = styled.p<any>`
  font-family: Proxima Nova;
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin: -2px 0;
  line-height: 20px;

  white-space: normal;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  ${({ isSmallScreen }): string =>
    isSmallScreen &&
    `
    font-size: 16px;
    font-weight: 500;
    margin: 0px 15px;
  `}

  ${({ simpleTextTopBar, isSmallScreen }): string =>
    simpleTextTopBar &&
    isSmallScreen &&
    `
    margin: 10px 15px;
  `}

  ${({ underline }): string =>
    underline &&
    `
    display: block;
    border-bottom: #ffffff 1px solid;
  `}

  ${({ entity }): string =>
    entity &&
    `
    font-size: 13px;
    font-weight: 500;
    margin: 0 15px;
    letter-spacing: 0.5px;
  `}

  &::first-letter {
    text-transform: uppercase;
  }

  span {
    font-size: 13px;
  }
`;

export const ArtworkPositioner = styled('div')<any>`
  flex-shrink: 0;
  div {
    width: ${({ isSmallScreen }): string => (isSmallScreen ? '30px' : '45px')};
    height: ${({ isSmallScreen }): string => (isSmallScreen ? '30px' : '45px')};
  }

  @media screen and (min-width: 47em) {
    margin-right: 8px;
  }
`;

export const InfoWrapper = styled('div')<{
  flexDirection?: 'column' | 'row';
  marginLeft?: string;
}>`
  display: flex;
  flex-direction: ${({ flexDirection }): string =>
    flexDirection ? flexDirection : 'row'};
  align-items: center;
  margin-left: ${({ marginLeft }): string => (marginLeft ? marginLeft : '1em')};
`;

export const TextWrapper = styled.div<ThemeProps<'div'>>`
  font-size: 13px !important;
  line-height: 15px;
  cursor: pointer;
  @media only screen and (max-width: 456px) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: calc(100vw - 170px);
  }
  opacity: ${(props): number => props.opacity};
  white-space: normal;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

export const LogoWrapper = styled('div')`
  margin-right: 1em;
`;

export const LogoFlexWrapper = styled(LogoWrapper)`
  display: flex;
`;

export const DSPChartToolbarIcon = styled(DSPCharts)`
  width: 20px;
  height: 23px;
  g {
    fill: ${({ theme }): string => theme.White};
  }
`;

export const ReleaseTypeWrapper = styled('span')`
  &:before {
    font-size: 14px;
    content: '•';
    margin: 0 5px;
  }
`;

export const TopicSection = styled('div')<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 25px;
  min-width: 200px;
  position: relative;
  flex-grow: 1;
  justify-content: center;

  &:first-of-type {
    padding-left: 1rem;
  }
  &:last-of-type {
    padding-right: 1rem;
  }

  &:not(:first-of-type) {
    &:before {
      content: '';
      display: block;
      background-color: ${({ theme }): string => theme.White};
      opacity: 0.47;
      width: 1px;
      height: 100%;
      position: absolute;
      left: 0;
    }

    padding: ${({ isSmallScreen, isTopLists }): string =>
      isSmallScreen && isTopLists ? '0' : '0 30px'};
  }

  ${FlagImage} {
    width: 22px;
    height: 15px;
  }

  ${StyledGlobe} {
    color: ${({ theme }): string => theme.Lime};
    width: 22px;
    height: 22px;
  }

  ${StyledIcon} {
    height: 1.8rem;
    width: 1.8rem;
    min-width: 1.8rem;
  }
  ${({ topList }): string =>
    topList &&
    `
    @media screen and (min-width: 50em) {
      flex-grow: 0;
    }
  `}
`;

export const TopicTitle = styled(EntityTypeTitle)`
  font-weight: 600;
  margin: 0;
  white-space: pre-wrap;
  letter-spacing: 0.7px;
  line-height: 1.2;
`;

export const FilterTitle = styled(EntityTypeTitle)`
  font-weight: 400;
  font-size: 16px;
  color: #ffffff;
  margin: 0;
  text-transform: capitalize;
`;

export const BlockWrapper = styled('div')<any>`
  display: flex;
  flex-direction: column;
  ${({ alignItems }): string =>
    alignItems &&
    `
    align-items: center;
    flex-direction: row;
  `}
  ${({ resetButton }): string =>
    resetButton &&
    `
    align-items: center;
    flex-direction: row;
  `}
`;

export const VerticalLine = styled('div')<any>`
  display: flex;
  height: 41px;
  width: 0px;
  border-left: 1px solid rgba(255, 255, 255, 0.28);
  margin: ${({ margin }): string => (margin ? margin : '0 30px')};
`;

export const GorizontalLine = styled('div')<any>`
  position: fixed;
  height: 0px;
  left: 0;
  width: 100%;
  margin-top: ${({ marginTop }): string => (marginTop ? marginTop : '5px')};
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

export const SmallScreenHeaderWrapper = styled('div')<any>`
  display: flex;
  width: 100%;
  align-items: center;
  margin-bottom: 20px;
`;

export const SmallScreenBodyWrapper = styled('div')<any>`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: start;
`;

export const Caret = styled(ToggleCaret)<any>`
  color: #ffffff;
  transform: rotate(90deg);
  margin-right: 15px;
`;

export const ArtistsWrapper = styled('div')<any>`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export const MenuWrapper = styled('div')<any>`
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin: 18px 0 0 18px;
`;

export const ButtonVariant = styled('button')<any>`
  height: ${({ height }): string => (height ? height : '32px')};
  display: flex;
  border: ${({ theme }): string => `solid 1px ${theme.colors.AccentGreen}`};
  width: 100%;
  justify-content: center;
  align-items: center;
  cursor: default;
  margin: ${({ theme, margin }): string =>
    margin ? margin : `${theme.spacing.small} 0`};

  &:last-of-type {
    margin: 0;
  }

  &:hover {
    background: ${({ theme }): string => theme.colors.Sand};
  }

  span {
    font-size: 11px;
    letter-spacing: 0.42px;
    font-weight: 600;
    color: ${({ theme }): string => theme.colors.NightBlue};
    margin-left: ${({ theme }): string => theme.spacing.small};
  }

  &:disabled {
    background: none;
    border: solid 1px #8da5bf;
    span {
      color: #8da5bf;
    }
    svg {
      color: #8da5bf;
    }
    g > g > g > g > g {
      fill: #8da5bf;
    }
    circle {
      stroke: #8da5bf;
    }
  }
`;

export const FiltersCircle = styled(Span)`
  background: #26d5a8;
  width: 10px;
  height: 10px;
  border-radius: 5px;

  margin-right: 10px;
`;
